import React from "react";
import { CheckSquare, ListChecks, Loader2, Circle, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "../../../lib/utils";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";
import { ToolCardProgress } from "./ToolCardProgress";

type TodoStatus = "in_progress" | "pending" | "completed" | "cancelled";
type TodoPriority = "high" | "medium" | "low";

interface TodoItem {
  content?: string;
  status?: TodoStatus;
  priority?: TodoPriority;
}

const PRIORITY_CONFIG: Record<TodoPriority, { dot: string; label: string }> = {
  high: { dot: "bg-red-500", label: "高" },
  medium: { dot: "bg-yellow-500", label: "中" },
  low: { dot: "bg-gray-400", label: "低" },
};

const STATUS_ICON: Record<TodoStatus, React.ReactNode> = {
  in_progress: <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />,
  pending: <Circle className="h-3.5 w-3.5 text-muted-foreground" />,
  completed: <CheckCircle2 className="h-3.5 w-3.5 text-success" />,
  cancelled: <XCircle className="h-3.5 w-3.5 text-muted-foreground" />,
};

function extractTodos(data: unknown): TodoItem[] {
  if (Array.isArray(data)) return data;
  if (typeof data === "object" && data !== null) {
    const r = data as Record<string, unknown>;
    if (Array.isArray(r.todos)) return r.todos;
    if (Array.isArray(r.items)) return r.items;
    if (Array.isArray(r.tasks)) return r.tasks;
  }
  return [];
}

function countByStatus(todos: TodoItem[]): Record<TodoStatus, number> {
  const counts: Record<TodoStatus, number> = { in_progress: 0, pending: 0, completed: 0, cancelled: 0 };
  for (const t of todos) {
    const s = t.status || "pending";
    if (s in counts) counts[s]++;
  }
  return counts;
}

const TodoWriteSummary: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const todos = extractTodos(args.todos);
  const counts = countByStatus(todos);
  const total = todos.length || 1;
  const completedPct = Math.round((counts.completed / total) * 100);
  const inProgressTask = todos.find((t) => t.status === "in_progress");

  return (
    <div className="px-4 py-2 space-y-2">
      {inProgressTask?.content && (
        <div className="text-xs text-foreground truncate">
          {inProgressTask.content}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {counts.in_progress > 0 && <span className="text-primary">进行中 {counts.in_progress}</span>}
        {counts.pending > 0 && <span>待处理 {counts.pending}</span>}
        {counts.completed > 0 && <span className="text-success">已完成 {counts.completed}</span>}
        {counts.cancelled > 0 && <span>已取消 {counts.cancelled}</span>}
      </div>
      <ToolCardProgress value={completedPct} />
    </div>
  );
};

const TodoWriteList: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const todos = extractTodos(args.todos);
  if (todos.length === 0) return null;

  return (
    <div className="px-4 py-2 space-y-1.5">
      {todos.map((todo, i) => {
        const status = todo.status || "pending";
        const priority = todo.priority;
        const pConfig = priority ? PRIORITY_CONFIG[priority] : null;

        return (
          <div key={i} className="flex items-start gap-2 text-xs">
            <span className="mt-0.5 flex-shrink-0">{STATUS_ICON[status]}</span>
            <span
              className={cn(
                "flex-1",
                status === "cancelled" && "line-through text-muted-foreground",
                status === "completed" && "text-muted-foreground"
              )}
            >
              {todo.content || `任务 ${i + 1}`}
            </span>
            {pConfig && (
              <span className="flex items-center gap-1 flex-shrink-0">
                <span className={cn("inline-block w-1.5 h-1.5 rounded-full", pConfig.dot)} />
                <span className="text-muted-foreground">{pConfig.label}</span>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TodoReadSummary: React.FC<{ result: unknown }> = ({ result }) => {
  const todos = extractTodos(result);
  const counts = countByStatus(todos);
  const total = todos.length || 1;
  const completedPct = Math.round((counts.completed / total) * 100);

  return (
    <div className="px-4 py-2 space-y-2">
      <div className="text-xs text-muted-foreground">
        共 {todos.length} 项 · 完成率 {completedPct}%
      </div>
      {todos.length > 0 && <ToolCardProgress value={completedPct} />}
      {todos.length > 0 && (
        <div className="space-y-1">
          {todos.map((todo, i) => {
            const status = todo.status || "pending";
            return (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="mt-0.5 flex-shrink-0">{STATUS_ICON[status]}</span>
                <span
                  className={cn(
                    "flex-1 truncate",
                    status === "cancelled" && "line-through text-muted-foreground",
                    status === "completed" && "text-muted-foreground"
                  )}
                >
                  {todo.content || `任务 ${i + 1}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const TaskCard: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const cardStatus: CardStatus = toolCall.status || "detected";
  const toolName = toolCall.toolName;
  const args = toolCall.args || {};

  if (toolName === "todo_read") {
    return (
      <ToolCardShell
        toolName="读取任务清单"
        status={cardStatus}
        icon={<ListChecks className="h-4 w-4" />}
        iconColor="text-blue-500"
        defaultExpanded={false}
      >
        <TodoReadSummary result={toolCall.result} />
        {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
          <ToolOutput data={toolCall.result} error={toolCall.error} />
        ) : null}
      </ToolCardShell>
    );
  }

  const todos = extractTodos(args.todos);
  const hasInProgress = todos.some((t) => t.status === "in_progress");

  return (
    <ToolCardShell
      toolName="任务清单"
      status={cardStatus}
      icon={<CheckSquare className="h-4 w-4" />}
      iconColor="text-blue-500"
      defaultExpanded={hasInProgress}
    >
      <TodoWriteSummary args={args} />
      <TodoWriteList args={args} />
      {toolCall.args && Object.keys(toolCall.args).length > 0 && (
        <ToolInput data={toolCall.args} />
      )}
      {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
        <ToolOutput data={toolCall.result} error={toolCall.error} />
      ) : null}
    </ToolCardShell>
  );
};
